import type { HistoryMessageModel } from '@mindverse/accessor-open';
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';

import SmallDotsLoading from '@/components/chat-app/components/SmallDotsLoading';

import ContextBasicInfo from '../../../../../context/BasicInfo';
import delay from '../../../../../utils/delay';
import { INNER_EVENT } from '../../../../../utils/utils';
import type { MessageType } from '../../../model/message-item';
import { transformSplitHintMsg } from '../../../model/message-item';
import styles from './index.less';

interface IProps {
  foldLoadMore(time: number): void;
  fetchHistory: () => Promise<HistoryMessageModel>;
}

function LoadMore(props: IProps): JSX.Element | null {
  const { foldLoadMore, fetchHistory } = props;
  const eleRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const loadingRef = useRef<boolean>(false);

  const [noMore, setNoMore] = useState<boolean>(false);
  const { mindId, sessionId } = useContext(ContextBasicInfo);
  const lastSessionIdRef = useRef<string>(sessionId || '');

  useEffect(() => {
    lastSessionIdRef.current = sessionId || '';
  }, [sessionId]);

  /**
   * 按照 同一个 session 内的消息纬度获取历史消息
   */
  const fetchCurrentSessionHistory: () => Promise<{ msgList: MessageType[]; noMore: boolean }> =
    useCallback(() => {
      return fetchHistory().then((res) => {
        return { msgList: transformSplitHintMsg(res.msgList), noMore: res.noMore };
      });
    }, [fetchHistory]);

  const fetchChatHistory = useCallback(() => {
    return fetchCurrentSessionHistory().then((res) => {
      if (res.noMore === true || res.msgList.length === 0) {
        setNoMore(true);
      }

      return Promise.resolve(res);
    });
  }, [fetchCurrentSessionHistory]);

  useEffect(() => {
    if (!eleRef.current) {
      return;
    }

    function callback(entries: IntersectionObserverEntry[]) {
      const entry = entries[0];

      if (entry.isIntersecting && lastSessionIdRef.current && !loadingRef.current) {
        if (noMore) {
          return;
        }

        setLoading(true);
        loadingRef.current = true;

        Promise.all([fetchChatHistory(), delay(200)])
          .then(([res]) => {
            loadingRef.current = false;

            if (res.msgList.length !== 0) {
              dispatchEvent(
                new CustomEvent(INNER_EVENT.HISTORY_MSG, {
                  detail: { list: res.msgList, noMore: res.noMore }
                })
              );
            }

            if (res.noMore) {
              foldLoadMore(200);
            }
          })
          .catch(() => {
            loadingRef.current = false;
            foldLoadMore(200);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }

    const observer = new IntersectionObserver(callback, {
      threshold: [0.99]
    });

    observer.observe(eleRef.current);

    return () => {
      observer.disconnect();
    };
  }, [noMore, mindId, foldLoadMore, fetchChatHistory]);

  return (
    <div ref={eleRef} className={styles.loadMore}>
      {loading ? <SmallDotsLoading className={styles.historyLoading} /> : null}
    </div>
  );
}

export default memo(LoadMore);
